import { useCallback, useEffect, useRef, useState } from "react";
import { getRequest } from "helpers/utility/axiosServiceLayer";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import { isOnPremUser } from "helpers/utility/utilities";

const HOST_URL = process.env.REACT_APP_API_ENDPOINT!;

export interface ScanResultsAvailability {
  /** All three below hold - the only thing the UI should gate on. */
  available: boolean;
  /** On-premise deployment with the feature switched on. */
  enabled: boolean;
  /** Enabled, and an address to talk to. */
  configured: boolean;
  /** Configured, and something answered there. */
  reachable: boolean;
}

const UNAVAILABLE: ScanResultsAvailability = {
  available: false,
  enabled: false,
  configured: false,
  reachable: false,
};

/**
 * Whether importing a dataset from DataServer scan results will actually work.
 *
 * Asked of the backend rather than inferred from the build: REACT_APP_ENVIRONMENT only
 * says this is an on-prem build, while whether DataServer is switched on, addressed and
 * answering is per-deployment state that only the server knows.
 *
 * These endpoints are not in the OpenAPI codegen, so this follows the hand-rolled axios
 * pattern of useScanResults rather than a generated react-query hook.
 */
export default function useScanResultsAvailability() {
  const [availability, setAvailability] =
    useState<ScanResultsAvailability | null>(null);
  // The click that needs this may land before the request resolves, so keep the promise
  // around to await rather than guessing.
  const inFlight = useRef<Promise<ScanResultsAvailability> | null>(null);

  const load = useCallback(() => {
    if (inFlight.current) return inFlight.current;

    // A cloud build has no DataServer to ask about; skip the round trip entirely.
    if (!isOnPremUser()) {
      inFlight.current = Promise.resolve(UNAVAILABLE);
      return inFlight.current;
    }

    inFlight.current = getRequest<ScanResultsAvailability>(
      composeApiURL(HOST_URL, "/scan-results/availability"),
    ).catch(() => UNAVAILABLE); // an option we cannot validate is not one we offer
    return inFlight.current;
  }, []);

  useEffect(() => {
    let cancelled = false;
    load().then((result) => {
      if (!cancelled) setAvailability(result);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  /** Resolves the current verdict, waiting for the in-flight request if needed. */
  const ensureAvailability = useCallback(async () => {
    if (availability) return availability;
    return load();
  }, [availability, load]);

  return {
    availability,
    available: availability?.available ?? false,
    isLoading: availability === null,
    ensureAvailability,
  };
}
